(() => {
  if (window.afrikaShippingRequestProperties) return;
  const configElement = document.getElementById('shipping-request-property-config');
  if (!configElement) return;
  const config = JSON.parse(configElement.textContent);
  let pending;
  let loading = 0;
  let errorNotice;
  let checkoutPending = false;
  let lastActivity = 0;
  const approvedForms = new WeakSet();
  const walletStates = new Map();
  const rowStates = new Map();
  const root = window.Shopify?.routes?.root || '/';
  const cartUrl = window.routes?.cartUrl || root + 'cart';
  const changeUrl = window.routes?.cartChangeUrl || root + 'cart/change';

  function cartMetadata() {
    const element = document.querySelector('[data-section-type="cart"] [data-shipping-request-lines]') || document.querySelector('#mini-cart [data-shipping-request-lines]') || document.querySelector('[data-shipping-request-lines]');
    return element ? JSON.parse(element.textContent) : { variantIds: [], missing: false };
  }

  function eligibleVariants() {
    return new Set(cartMetadata().variantIds.map(String));
  }

  function setRowsReady(ready) {
    if (!ready) {
      const elements = Array.from(document.querySelectorAll('#mini-cart .mini-cart__line-item, [data-section-type="cart"] .line-item'));
      ['cart-form', 'mini-cart'].forEach(id => {
        const form = document.getElementById(id);
        if (form) elements.push(...Array.from(form.elements).filter(element => element.name));
      });
      elements.forEach(element => {
        if (!rowStates.has(element)) rowStates.set(element, element.inert);
        element.inert = true;
      });
    } else {
      rowStates.forEach((inert, element) => { element.inert = inert; });
      rowStates.clear();
    }
  }

  function setWalletsReady(ready) {
    const selector = '#mini-cart .additional-checkout-buttons, [data-section-type="cart"] .additional-checkout-buttons, #mini-cart shopify-accelerated-checkout-cart, [data-section-type="cart"] shopify-accelerated-checkout-cart';
    if (!ready) {
      document.querySelectorAll(selector).forEach(element => {
        if (!walletStates.has(element)) walletStates.set(element, element.inert);
        element.inert = true;
      });
    } else {
      walletStates.forEach((inert, element) => { element.inert = inert; });
      walletStates.clear();
    }
  }

  async function requestJson(url, body) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 12000);
    try {
      const response = await fetch(url, {
        credentials: 'same-origin',
        cache: 'no-store',
        signal: controller.signal,
        ...(body ? { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: JSON.stringify(body) } : {})
      });
      if (!response.ok) throw new Error('Cart request failed: ' + response.status);
      const cart = await response.json();
      if (!Array.isArray(cart.items)) throw new Error('Invalid cart response');
      return cart;
    } finally {
      clearTimeout(timeout);
    }
  }

  async function waitForCartIdle() {
    const started = Date.now();
    let areaPending;
    do {
      areaPending = window.afrikaAreaCartPending;
      if (areaPending) {
        let timeout;
        try {
          await Promise.race([areaPending, new Promise((resolve, reject) => { timeout = setTimeout(() => reject(new Error('Area cart update is still pending')), 12000); })]);
        } finally { clearTimeout(timeout); }
      }
      while (loading > 0 || Date.now() - lastActivity < 150) {
      if (Date.now() - started > 12000) throw new Error('Cart update is still pending');
      await new Promise(resolve => setTimeout(resolve, 50));
      }
    } while (window.afrikaAreaCartPending !== areaPending);
  }

  function refreshCart() {
    if (window.theme?.cartType === 'page') return Promise.resolve();
    return new Promise((resolve, reject) => {
      const finish = () => { clearTimeout(timeout); document.removeEventListener('cart:rerendered', finish, true); resolve(); };
      const timeout = setTimeout(() => { document.removeEventListener('cart:rerendered', finish, true); reject(new Error('Cart refresh timed out')); }, 12000);
      document.addEventListener('cart:rerendered', finish, true);
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { scrollToTop: false } }));
    });
  }

  async function synchronize() {
    const eligible = eligibleVariants();
    if (!eligible.size) return false;
    await waitForCartIdle();
    let cart = await requestJson(cartUrl + '.js');
    let changed = false;
    const maximum = cart.items.length + 5;
    for (let attempt = 0; attempt < maximum; attempt++) {
      const item = cart.items.find(line => eligible.has(String(line.variant_id)) && !String(line.properties?.[config.key] || '').trim());
      if (!item) {
        if (changed) await refreshCart();
        return changed;
      }
      await waitForCartIdle();
      cart = await requestJson(changeUrl + '.js', {
        id: item.key,
        quantity: item.quantity,
        properties: { ...(item.properties || {}), [config.key]: config.value }
      });
      changed = true;
    }
    throw new Error('Shipping notice could not be verified');
  }

  function showError(form, retryAction) {
    if (errorNotice) errorNotice.remove();
    errorNotice = document.createElement('div');
    errorNotice.className = 'alert alert--error';
    errorNotice.setAttribute('role', 'alert');
    const message = document.createElement('p');
    message.textContent = config.error;
    const retry = document.createElement('button');
    retry.type = 'button';
    retry.className = 'button button--secondary';
    retry.textContent = config.retry;
    retry.addEventListener('click', () => {
      if (retryAction) { retryAction(); return; }
      const current = form && document.getElementById(form.id);
      if (current) current.requestSubmit(Array.from(current.elements).find(element => element.name === 'checkout'));
      else ensure().catch(() => showError());
    });
    errorNotice.append(message, retry);
    const current = form && document.getElementById(form.id);
    const visibleButton = current && Array.from(current.elements).find(element => element.name === 'checkout' && element.getClientRects().length);
    const target = visibleButton?.parentElement || document.querySelector('[data-section-type="cart"] .container') || document.querySelector('#mini-cart .mini-cart__cta-stack') || document.getElementById('main') || document.body;
    target.appendChild(errorNotice);
    errorNotice.scrollIntoView({ block: 'nearest' });
  }

  function ensure() {
    if (pending) return pending;
    if (!eligibleVariants().size) return Promise.resolve(false);
    setWalletsReady(false);
    setRowsReady(false);
    pending = synchronize().then(changed => {
      if (errorNotice) errorNotice.remove();
      errorNotice = undefined;
      setWalletsReady(true);
      return changed;
    }).finally(() => { pending = undefined; setRowsReady(true); });
    return pending;
  }

  function snapshotForm(form) {
    return Array.from(form.elements).filter(element => element.name).map(element => ({
      name: element.name,
      type: element.type,
      value: element.value,
      checked: element.checked,
      disabled: element.disabled
    }));
  }

  function restoreForm(form, snapshot) {
    const used = new Map();
    snapshot.forEach(field => {
      const matches = Array.from(form.elements).filter(element => element.name === field.name && element.type === field.type);
      const index = used.get(field.name + ':' + field.type) || 0;
      const target = matches[index];
      used.set(field.name + ':' + field.type, index + 1);
      if (!target) return;
      target.value = field.value;
      if (field.type === 'checkbox' || field.type === 'radio') target.checked = field.checked;
      target.disabled = field.disabled;
    });
  }

  document.addEventListener('submit', event => {
    const form = event.target;
    if (!form || (form.id !== 'cart-form' && form.id !== 'mini-cart') || event.submitter?.name !== 'checkout') return;
    if (approvedForms.has(form)) { approvedForms.delete(form); return; }
    if (!eligibleVariants().size) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (checkoutPending) return;
    checkoutPending = true;
    const snapshot = snapshotForm(form);
    ensure().then(() => {
      const current = document.getElementById(form.id);
      if (!current) throw new Error('Checkout form no longer exists');
      restoreForm(current, snapshot);
      const submitter = Array.from(current.elements).find(element => element.name === 'checkout' && !element.disabled);
      if (!submitter) throw new Error('Checkout button is unavailable');
      approvedForms.add(current);
      try { current.requestSubmit(submitter); } finally { approvedForms.delete(current); }
    }).catch(error => {
      console.warn('[shipping-request-property]', error.message);
      const current = document.getElementById(form.id);
      if (current) restoreForm(current, snapshot);
      showError(form);
    }).finally(() => { checkoutPending = false; });
  }, true);

  function followCheckoutLink(url) {
    if (checkoutPending) return;
    checkoutPending = true;
    ensure().then(() => window.location.assign(url.href)).catch(error => {
      console.warn('[shipping-request-property]', error.message);
      showError(undefined, () => followCheckoutLink(url));
    }).finally(() => { checkoutPending = false; });
  }

  document.addEventListener('click', event => {
    const link = event.target.closest('a[href]');
    if (!link || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || link.target === '_blank' || !eligibleVariants().size) return;
    const url = new URL(link.href, window.location.href);
    if (url.origin !== window.location.origin || !/^\/(?:[a-z]{2}(?:-[A-Z]{2})?\/)?checkouts?(?:\/|$)/.test(url.pathname)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    followCheckoutLink(url);
  }, true);

  function updateWallets() {
    if (pending) { setWalletsReady(false); setRowsReady(false); }
    else setWalletsReady(!cartMetadata().missing);
  }

  document.addEventListener('theme:loading:start', () => { loading++; lastActivity = Date.now(); });
  document.addEventListener('theme:loading:end', () => { loading = Math.max(0, loading - 1); lastActivity = Date.now(); });
  document.addEventListener('cart:rerendered', updateWallets, true);
  window.addEventListener('pageshow', updateWallets);
  window.afrikaShippingRequestProperties = { ensure };
  updateWallets();
})();
